import { ContainerTask } from '../types/container'
import { mapBackendStatusToFrontend, mapBackendTaskTypeToFrontend } from '../services/taskApi'

/**
 * Backend task row → ContainerTask, the shape every task card renders from.
 *
 * The dispatch board, Driver Tasks and Driver Management all show the same task
 * card now, so they must build the card's input the same way too — the route
 * cells read origin/destination node fields that a hand-rolled partial object
 * silently leaves blank, which is how those pages drifted apart in the first
 * place.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function backendTaskToContainer(task: any): ContainerTask {
  return {
    id: String(task.id),
    taskId: task.task_id || `TASK-${String(task.id).padStart(6, '0')}`,
    containerNumber: task.container_number || '',
    // 订舱号。出口单在提柜之前**根本没有柜号**（柜是到码头才领的），这个号是那段
    // 时间里唯一认得出这一票的东西 —— 漏了它，板上就是一行没有号码的空行，搜索
    // 框里也等于不存在。接口一直在发（getAll 选了 o.booking_number），只是这份
    // 精简转换从来没接。另一份 useDispatchTasks.transformBackendToFrontend 早就有，
    // 两份必须一起加，见 [[project_task_transform_partial_copies]]。
    bookingNumber: task.booking_number || '',
    shippingLine: task.shipping_line || '',
    shippingLineCode: task.shipping_line || '',
    taskType: mapBackendTaskTypeToFrontend(task.task_type) as ContainerTask['taskType'],
    taskSubtype: (task.task_subtype || 'drop') as ContainerTask['taskSubtype'],
    status: mapBackendStatusToFrontend(task.status) as ContainerTask['status'],
    // B3 Fix Plate — server-derived, see PLATE_FIX_APPOINTMENT_ID_SQL
    plateFixAppointmentId: task.plate_fix_appointment_id ?? null,
    // 海关把柜子拉去查验了（Transfer for Exam）。服务端从柜子监控的扣柜标签算出来，
    // On Hold 列表靠它把这类柜子单独分组 —— 它们和普通扣柜的处置完全不同。
    customsExam: !!task.customs_exam,
    // 查验站那边的免费期最后一天（PCC 查询页的 Last Free Day）。柜子进 CBP Hold
    // 之后码头那条监控就停了，下面那个 lfd 从此冻住不动 —— 这段时间还在计钱的
    // 是 PCC 这个日子。两份转换函数都要加。
    pccLastFreeDay: task.pcc_lfd || null,
    // 只差一笔码头费就能放行 —— 调度员付掉当场就能提，是 On Hold 里唯一
    // 能马上动手的一类。分组时排在最前面。
    awaitingGateFee: !!task.awaiting_gate_fee,
    gateFeePaid: !!task.gate_fee_paid,
    // 封闭区：柜子在码头但拿不出来；recalledFromDispatch 表示它是从已派单
    // 状态被拉回来的（可能有司机在路上）。
    closedArea: !!task.closed_area,
    recalledFromDispatch: !!task.recalled_from_dispatch,
    // 柜子为什么被扣着 —— 码头那几句原话，服务端从柜子监控行一次打包回来。
    // On Hold 那一行在状态底下显示它。两份转换函数都要加。
    holdReasons: task.hold_info?.tags ?? [],
    holdDetails: task.hold_info?.details ?? [],
    terminalHoldCodes: task.hold_info?.terminal_codes ?? null,
    holdCheckedAt: task.hold_info?.checked_at ?? null,
    holdMonitorActive: task.hold_info?.monitor_status === 'active',
    // 提柜那张票已经来不及了（封闭区解除时判的）—— Route 列显示 Rebook。
    portApptNeedsRebook: !!task.port_appt_needs_rebook,
    portApptRebookSides: (task as { port_appt_rebook_sides?: string[] }).port_appt_rebook_sides || [],
    driver: task.driver_name || 'Unassigned',
    driverAvatar: '/placeholder.svg?height=32&width=32',
    // The plate the terminal will see for this task. Every page that opens the
    // task modal has to carry it, or the driver panel falls back to whatever
    // else is lying around — which is how a driving licence ended up displayed
    // as a truck plate.
    truckPlate: task.truck_plate || '',
    portAppointment: task.port_appointment_time || '',
    portAppointmentAllDay: !!task.port_appointment_all_day,
    // Read off the pickup leg by the server — the only date a return leg can be
    // sorted by, since it never has an appointment of its own.
    pickupGateOutTime: task.pickup_gate_out_time || undefined,
    pickupGateOutSource: task.pickup_gate_out_source || undefined,
    lfd: task.lfd || '',
    shipper: task.shipper_name || '',
    warehouse: task.warehouse_name || '',
    warehouseAddress: task.warehouse_address || '',
    warehouseAppointment: task.warehouse_appointment || '',
    warehouseAppointmentAllDay: !!task.warehouse_appointment_all_day,
    internalNote: task.dispatcher_notes || '',
    containerSize: task.container_size || '40ft',
    // 同 useDispatchTasks：空就是空，不兜 'dry'。画之前走 containerTypeLabel。
    containerType: (task.container_type || undefined) as ContainerTask['containerType'],
    // 要哪种发电机。后端可能回 null（没人说过）或 'none'（明确不要），两者都不画
    // 图标；只有 'genset' / 'genset_chassis' 会画，而且画的是**两种不同的**图标。
    gensetType: task.genset_type || undefined,
    chassisNumber: task.chassis_number || '',
    importExport: 'import' as ContainerTask['importExport'],
    originPort: task.port_name || '',
    portName: task.port_name || '',
    portShortName: task.port_short_name || '',
    warehouseName: task.warehouse_name || '',
    warehouseShortName: task.warehouse_short_name || '',
    yardName: task.yard_name || '',
    yardShortName: task.yard_short_name || '',
    // ⚠️ 地点的 **id 必须和名字一起带下来**。
    //
    // 这里原本只带了 type 和 name。名字够画一行路线，但凡是要**比较两条任务是不是
    // 同一个地点**的逻辑全都失效了 —— 智能排序的一提一还就是这么静默失灵的：判
    // 码头永远拿到 null，配对在第一关就被挡掉，界面上不报错，只是"一对也配不出来"。
    // 后端列表查询本来就是 `dt.*`，这些字段一直在响应里，是这份转换函数没往下传。
    // 另一份转换函数 hooks/useDispatchTasks.ts 是带的 —— 两份各自维护，这就是代价。
    portId: task.port_id ?? null,
    orderId: task.order_id ?? undefined,
    originType: task.origin_type || undefined,
    originId: task.origin_id ?? undefined,
    originName: task.origin_name || '',
    originShortName: task.origin_short_name || '',
    originCity: task.origin_city || '',
    originAddress: task.origin_address || '',
    destinationType: task.destination_type || undefined,
    destinationId: task.destination_id ?? undefined,
    destinationName: task.destination_name || '',
    destinationShortName: task.destination_short_name || '',
    destinationCity: task.destination_city || '',
    destinationAddress: task.destination_address || '',
    returnLocationType: task.return_location_type || undefined,
    returnLocationId: task.return_location_id ?? undefined,
    returnLocationName: task.return_location_name || '',
    returnLocationShortName: task.return_location_short_name || '',
    returnLocationPending: task.return_location_pending || false,
    // 坐标：智能排序第一趟要按「离公司车场多近」挑空柜。缺了它不会报错，
    // 只是那条规则静默退化成"保持原顺序"——同样是看不出来的失效。
    originZoneId: task.origin_zone_id ?? undefined,
    destinationZoneId: task.destination_zone_id ?? undefined,
    originLat: task.origin_lat ? parseFloat(String(task.origin_lat)) : undefined,
    originLng: task.origin_lng ? parseFloat(String(task.origin_lng)) : undefined,
    destinationLat: task.destination_lat ? parseFloat(String(task.destination_lat)) : undefined,
    destinationLng: task.destination_lng ? parseFloat(String(task.destination_lng)) : undefined,
    returnLocationZoneId: task.return_location_zone_id ?? undefined,
    returnLocationLat: task.return_location_lat ? parseFloat(String(task.return_location_lat)) : undefined,
    returnLocationLng: task.return_location_lng ? parseFloat(String(task.return_location_lng)) : undefined,
    portAppointmentStatus: task.port_appointment_status || '',
    warehouseAppointmentStatus: task.warehouse_appointment_status || '',
    routeNodes: task.route_nodes || [],
    hasRejectedNode: task.has_rejected_node || false,
    specialInstructions: task.dispatcher_instructions || '',
    gateCode: task.gate_code || '',
    sealNumber: task.seal_number || '',
    currentLocation: task.current_location || '',
    lastUpdate: task.updated_at || task.created_at,
    // Pairing fields
    pairedTaskId: task.paired_task_id || undefined,
    pairedTaskDisplayId: task.paired_task_display_id || '',
    pairedTaskType: task.paired_task_type || '',
    pairedTaskSubtype: task.paired_task_subtype || '',
    pairedTaskStatus: task.paired_task_status || '',
    pairedContainerNumber: task.paired_container_number || '',
    // 司机卡里手排的位次（迁移 192）。漏了这两个字段，卡片会退回按预约时间排，
    // 而且看不出来是漏了——手排完刷新一下就"自己变回去"。
    driverSortDay: task.driver_sort_day ?? null,
    driverSortOrder: task.driver_sort_order ?? null,
    // 收尾那一天（迁移 212）。漏了它，跑完的活会退回「记在今天」，在派车确认
    // 窗口和司机卡的今天那一段里越堆越多。
    completedDay: task.completed_day ?? undefined,
  } as ContainerTask
}
